import styled, { css, keyframes } from "styled-components";
import theme from "../../../UI/theme";
import { useSubscription } from "../../../hooks/useSubscription";
import { subscriptionsTypes } from "../../../scripts/subscription/subscriptionTypes";
import LiveDecibelChartContainer from "./LiveDecibelChartContainer";
import { decibelWarningThreshold } from "../decibel/decibelInsightsUtils";
import { CardHeader } from "../insightsStyles";
import { updateZone } from "../../../scripts/zone";

const heartbeatShadow = keyframes`
  /* rest */
  0%   { box-shadow: inset 0 0 0 0 transparent; }

  /* beat 1 */
  6%   { box-shadow: inset 0 0 100px 0 ${theme.colors.raspberryRed}; }
  12%  { box-shadow: inset 0 0 30px 0 transparent; }

  /* beat 2 */
  18%  { box-shadow: inset 0 0 120px 0 ${theme.colors.raspberryRed}; }
  28%  { box-shadow: inset 0 0 20px 0 transparent; }

  /* pause */
  100% { box-shadow: inset 0 0 0 0 transparent; }
`;

const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 20px;

    @media only screen and (max-width: ${theme.screenSizes.medium - 1}px) {
        grid-gap: 10px;
    }
`

const ZoneGridContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 20px;

    @media only screen and (max-width: ${theme.screenSizes.medium - 1}px) {
        grid-template-columns: 1fr;
        grid-gap: 10px;
    }
`

const ZoneContainer = styled.div`
  background-color: ${theme.colors.darkSpace90};
  box-sizing: border-box;
  padding: 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px; /* grid-gap -> gap */
  align-items: center;
  color: ${theme.colors.textGray};
  overflow: hidden;

  /* default: no inset shadow at all */
  box-shadow: none;

  ${({ $alert }) =>
    $alert &&
    css`
      will-change: box-shadow;
      animation: ${heartbeatShadow} 4.1s ease-in-out infinite; /* bam-bam — pause */
    `}

  @media only screen and (max-width: ${theme.screenSizes.medium - 1}px) {
    padding: 10px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    box-shadow: none !important;
  }
`;

const LiveDecibelLabel = styled.label`
    color: ${theme.colors.greenEnergy};
    font-size: 2rem;
    font-weight: 100;
    white-space: nowrap;
    justify-self: end;

    ${props => props.$alert && css`
        color: ${theme.colors.raspberryRed};
    `}
`

const LiveInsights = ({ customer, }) => {
    const [zones, setZones] = useSubscription(subscriptionsTypes.zones);
    const [updatedZones, setUpdatedZones] = useSubscription(subscriptionsTypes.zones);
    const [zoneLives, setZoneLives] = useSubscription(subscriptionsTypes.zoneLives);
    const [sensors, setSensors] = useSubscription(subscriptionsTypes.sensors);

    const onZoneChange = (zoneId, newDb) => {

        let updatedZone = zones.find(z => z.zoneId === zoneId);
        updatedZone.dbWarningLimit = newDb;

        setUpdatedZones(prev => ({
            ...prev,
            [zoneId]: updatedZone
        }));

        updateZone(customer.customerId, { ...updatedZone });

    }

    return <Container>
        <ZoneGridContainer>
            {zones?.filter(zone => zone.shouldRegulate === 1 && zone.hide === 0 && sensors?.some(sensor => sensor?.zoneId === zone?.zoneId || sensor?.zoneId === zone?.backupZoneId))
                ?.sort((a, b) => a?.orderIndex > b?.orderIndex)
                ?.map(zone => {
                    const zoneLive = zoneLives?.find(zl => zl.zoneId === zone?.zoneId);
                    const currentLimit = updatedZones?.[zone?.zoneId]?.dbWarningLimit ?? zone?.dbWarningLimit ?? decibelWarningThreshold;
                    const alert = zoneLive?.averageDecibel > currentLimit;
                    return <ZoneContainer key={zone?.zoneId} $alert={alert}>
                        <CardHeader  >{zone?.zoneName}</CardHeader>
                        <LiveDecibelLabel
                            $alert={alert}
                        >
                            {zoneLive?.averageDecibel > 0 ? `${zoneLive?.averageDecibel?.toFixed(1)} dB` : 'N/A'}
                        </LiveDecibelLabel>
                        <LiveDecibelChartContainer
                            zone={zone}
                            zoneLive={zoneLive}
                            currentLimit={currentLimit}
                            onZoneChange={onZoneChange}
                        />
                    </ZoneContainer>
                })}
        </ZoneGridContainer>
    </Container>
}

export default LiveInsights;